/**
 * Type / size / count validation for a set of files, ahead of an upload.
 *
 * Extracted from the crew FileUploader's FileUploaderValidationManager, which took a
 * whole live component and read three numbers off it. This takes the three numbers.
 * The rules are unchanged — crew's manager is now a thin adapter over this class.
 */
export default class FileValidator {

    /**
     * @param {Object} options
     * @param {number} options.maxFiles - Maximum number of files in one submission.
     * @param {number} options.maxFileSize - Maximum size of any one file, in bytes.
     * @param {string[]} options.allowedFileTypes - MIME types (`image/jpeg`), wildcards
     *   (`image/*`) or extensions (`.jpg`). An empty list allows everything.
     */
    constructor({maxFiles, maxFileSize, allowedFileTypes} = {}) {
        this.maxFiles = maxFiles;
        this.maxFileSize = maxFileSize;
        this.allowedFileTypes = allowedFileTypes;
    }

    /**
     * @param {File[]} newFiles - Files being added.
     * @param {File[]} existingFiles - Files already attached.
     * @returns {{valid: boolean, validFiles: File[], errors: string[]}}
     */
    validateFiles(newFiles, existingFiles = []) {
        const errors = [];
        const validFiles = [];

        // Over the count cap, nothing is accepted — a partial accept would silently drop
        // files the person chose deliberately.
        const totalFileCount = existingFiles.length + newFiles.length;
        if (totalFileCount > this.maxFiles) {
            errors.push(`Maximum ${this.maxFiles} files allowed. You have selected ${totalFileCount} files.`);
            return {valid: false, validFiles: [], errors};
        }

        for (const file of newFiles) {
            const fileErrors = this.validateFile(file);

            if (fileErrors.length === 0) {
                validFiles.push(file);
            } else {
                errors.push(...fileErrors);
            }
        }

        return {
            valid: errors.length === 0,
            validFiles,
            errors
        };
    }

    /**
     * @param {File} file
     * @returns {string[]} Error messages; empty when the file is acceptable.
     */
    validateFile(file) {
        const errors = [];

        if (!this.isValidFileType(file)) {
            errors.push(`"${file.name}" is not an allowed file type. Allowed types: ${this.allowedFileTypes.join(', ')}`);
        }

        if (!this.isValidFileSize(file)) {
            const maxSizeMB = (this.maxFileSize / (1024 * 1024)).toFixed(1);
            const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);
            errors.push(`"${file.name}" is too large (${fileSizeMB}MB). Maximum size is ${maxSizeMB}MB.`);
        }

        return errors;
    }

    isValidFileType(file) {
        if (!this.allowedFileTypes || this.allowedFileTypes.length === 0) return true;
        if (this.allowedFileTypes.includes(file.type)) return true;

        const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
        return this.allowedFileTypes.some(type => {
            if (type.includes('*')) {
                const [category] = type.split('/');
                return file.type.startsWith(category + '/');
            }
            if (type.startsWith('.')) {
                return fileExtension === type.toLowerCase();
            }
            return false;
        });
    }

    isValidFileSize(file) {
        return file.size <= this.maxFileSize;
    }
}

/**
 * Human-readable file size, e.g. "2.41 MB".
 * @param {number} bytes
 * @returns {string}
 */
export function formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}
